/**
 * useWssData Hook
 * Handles fetching and managing WSS/territory data
 */

import { useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import { getUniqueListBy } from "@components/Common/Function";
import { myWssService } from "../services/wssService";
import { formatDate } from "../utils/gpsUtils";
import { getMyDiaryWSSDetails } from "@redux/actions/mydiaryDetails-actions";
import {
  LOADING_STATES,
  SPECIAL_USER_TYPES,
  MTP_ENABLED_USER_TYPES,
} from "../utils/wssConstants";

export const useWssData = ({
  selectedTerritory,
  selectLatLong,
  sh3CodeForTerritory,
  tourPlanDefaultData,
  clusterCodeOption,
  territoryOptions,
  userType,
  setWssOption,
  setIsWssData,
  showError,
  setTerritoryPickList,
  setCluster,
  setSH3CodeForTerritory,
  setSelectedTerritory,
  setLoading,
  setWss,
  setCustomerCode,
  setMtpCheckIn,
  setAccuracy,
  setWssLocation,
  setSelectedWssCode,
  setSelectedWSS,
}) => {
  const dispatch = useDispatch();

  /**
   * Fetch WSS list by territory
   */
  const fetchWssList = useCallback(
    async (territoryCode) => {
      if (!territoryCode) return;

      try {
        setIsWssData(LOADING_STATES.LOADING);

        const response = await myWssService.getWssList(territoryCode);

        if (response.success) {
          if (response.data.totalSize === 0) {
            setIsWssData(LOADING_STATES.NO_DATA);
          } else {
            setWssOption(response.data.records);
            setIsWssData(LOADING_STATES.SUCCESS);
          }
        } else {
          showError(response.message);
          setIsWssData(LOADING_STATES.NO_DATA);
        }
      } catch (error) {
        console.error("Error fetching WSS list:", error);
        showError("Failed to fetch WSS data");
        setIsWssData(LOADING_STATES.NO_DATA);
      }
    },
    [setWssOption, setIsWssData, showError]
  );

  /**
   * Fetch territories for ZSM users
   */
  const fetchZSMTerritories = useCallback(
    async (sh3Code) => {
      if (!sh3Code) return;

      try {
        const response = await myWssService.getZSMTerritories(sh3Code);
        if (response.success) {
          setTerritoryPickList(response.data);
        } else {
          setTerritoryPickList([]);
        }
      } catch (error) {
        console.error("Error fetching ZSM territories:", error);
        setTerritoryPickList([]);
      }
    },
    [setTerritoryPickList]
  );

  /**
   * Auto-populate cluster and territory from MTP (Monthly Tour Plan)
   */
  const setDefaultValues = useCallback(() => {
    if (!tourPlanDefaultData || tourPlanDefaultData.length === 0) return;

    const clusterFilterData = getUniqueListBy(
      clusterCodeOption,
      SPECIAL_USER_TYPES.includes(userType)
        ? "CLUSTER_CODE__c"
        : "SH3_Name__c"
    );

    const mtpCurrentDateData = tourPlanDefaultData.filter(
      (item) =>
        formatDate(item.MTP_Date__c) === formatDate(new Date()) 
      // &&
      //   item.Objectives_es__c &&
      //   item.Objectives_es__c.includes("WSS Visit")
    );

    if (!MTP_ENABLED_USER_TYPES.includes(userType) && 
        userType !== "SH3") {
      // For HO Auditor, HO Collaborator, NSM, and other user types:
      // still auto-populate territory from MTP (matches old MyWss.js else branch)
      if (mtpCurrentDateData.length > 0) {
        const mtpData = mtpCurrentDateData[0];
        if (mtpData.MTP_Territory_es__r) {
          const mtpTerritoryCode = mtpData.MTP_Territory_es__r.SH2_Code__c;
          const defaultTerritory = territoryOptions.filter(
            (item) => item.SH2_Code__c === mtpTerritoryCode
          );
          if (defaultTerritory && defaultTerritory.length > 0) {
            setSelectedTerritory(defaultTerritory[0]);
          }
        }
      }
      return;
    }

    if (mtpCurrentDateData.length > 0) {
      const mtpData = mtpCurrentDateData[0];

      if (MTP_ENABLED_USER_TYPES.includes(userType)) {
        // Set cluster from MTP (for MH3, RBDM, etc.)
        if (mtpData.Cluster_es__c) {
          const parts = mtpData.Cluster_es__c.split(":");
          let defaultCluster = clusterFilterData.filter(
            (item) => item.CLUSTER_CODE__c === parts[1]
          );

          if (!defaultCluster || defaultCluster.length === 0) {
            defaultCluster = clusterFilterData.filter(
              (item) => item.SH3_Code__c === parts[1]
            );
          }

          if (defaultCluster && defaultCluster.length > 0) {
            setCluster(defaultCluster[0]);
            setSH3CodeForTerritory(parts[1]);
          }
        }

        // Set territory from MTP
        if (mtpData.MTP_Territory_es__r) {
          const mtpTerritoryCode = mtpData.MTP_Territory_es__r.SH2_Code__c;
          let defaultTerritory = territoryOptions.filter(
            (item) => item.SH2_Code__c === mtpTerritoryCode
          );

          if (!defaultTerritory || defaultTerritory.length === 0) {
            defaultTerritory = []; // Will be fetched from ZSM territories
          }

          if (defaultTerritory && defaultTerritory.length > 0) {
            setSelectedTerritory(defaultTerritory[0]);
          }
        }
      } else {
        // For SH3 and others
        if (mtpData.MTP_Territory_es__r) {
          const mtpTerritoryCode = mtpData.MTP_Territory_es__r.SH2_Code__c;
          const defaultTerritory = territoryOptions.filter(
            (item) => item.SH2_Code__c === mtpTerritoryCode
          );

          if (defaultTerritory && defaultTerritory.length > 0) {
            setSelectedTerritory(defaultTerritory[0]);
          }
        }
      }
    }
  }, [
    tourPlanDefaultData,
    clusterCodeOption,
    territoryOptions,
    userType,
    setCluster,
    setSH3CodeForTerritory,
    setSelectedTerritory,
  ]);

  /**
   * Handle WSS selection and update location
   */
  useEffect(() => {
    if (selectLatLong && selectLatLong.Customer_Code__c) {
      // Extract lat/long from selected WSS
      let lat, lng;
      
      if (
        selectLatLong.Updated_WSS_longitude__c &&
        selectLatLong.Updated_WSS_latitude__c
      ) {
        lat = selectLatLong.Updated_WSS_latitude__c;
        lng = selectLatLong.Updated_WSS_longitude__c;
      } else {
        lat = selectLatLong.WSS_latitude__c;
        lng = selectLatLong.WSS_longitude__c;
      }

      // Update location hook
      setWssLocation(lat, lng);

      // Update other state
      setWss(selectLatLong.CustomerName__c);
      setMtpCheckIn(selectLatLong.Monthly_Tour_Plan_CheckIn__c);
      setCustomerCode(selectLatLong.Customer_Code__c);
      setAccuracy(parseInt(selectLatLong.GPSAccuracy__c) || 0);

      // Update parent component state if provided
      if (setSelectedWssCode) {
        setSelectedWssCode(selectLatLong.Customer_Code__c);
      }
      if (setSelectedWSS) {
        setSelectedWSS(selectLatLong.CustomerName__c);
      }

      // Dispatch My Diary WSS details
      dispatch(getMyDiaryWSSDetails(selectLatLong.Customer_Code__c));
    }
  }, [
    selectLatLong,
    setWssLocation,
    setWss,
    setMtpCheckIn,
    setCustomerCode,
    setAccuracy,
    setSelectedWssCode,
    setSelectedWSS,
    dispatch,
  ]);

  /**
   * Effect: Fetch WSS list when territory changes
   */
  useEffect(() => {
    if (selectedTerritory?.SH2_Code__c) {
      fetchWssList(selectedTerritory.SH2_Code__c);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTerritory?.SH2_Code__c]);

  /**
   * Effect: Fetch ZSM territories when sh3Code changes
   */
  useEffect(() => {
    if (sh3CodeForTerritory) {
      fetchZSMTerritories(sh3CodeForTerritory);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sh3CodeForTerritory]);

  /**
   * Effect: Set default values on mount
   */
  useEffect(() => {
    setDefaultValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    fetchWssList,
    fetchZSMTerritories,
    setDefaultValues,
  };
};

export default useWssData;
